import { Ref, RefObject, forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { ColorBullet } from '../../components/ColorBullet';
import { useClickOutside, useScrollRefEventListener } from '../../hooks';
import { DropdownNext, Icon, Text } from '../../ui';
import {
  StyledColorIconPrefix,
  StyledContainer,
  StyledDropdownOptionWrapper,
  StyledDropdownOptionsContainer,
  StyledIconWrapper,
  StyledLabelContainer,
  StyledPaletteContainer,
  StyledPaletteDropdown,
  StyledPaletteSelectorContainer,
} from './ColorSelect.style';

type ColorOption = {
  label: string;
  value: string;
};

export interface ColorSelectProps {
  customColorLabel?: string;
  limit: number;
  options: ColorOption[];
  transparentLabel?: string;
  defaultValue?: string;
  onChange?: (selectedValue: string) => void;
  variant?: 'default' | 'palette';
  placeholder?: string;
}

export const ColorSelect = forwardRef<HTMLDivElement, ColorSelectProps>(ColorSelectRefWrapper);

function ColorSelectRefWrapper(
  {
    customColorLabel,
    limit = 8,
    onChange,
    options,
    transparentLabel,
    defaultValue,
    variant = 'default',
    placeholder,
  }: ColorSelectProps,
  scrollRef?: Ref<HTMLDivElement>
) {
  const defaultSelected = useMemo((): ColorOption => {
    if (options.length === 0) {
      return { label: '#000000', value: '#000000' };
    }

    if (defaultValue === 'custom') {
      return { label: defaultValue, value: defaultValue };
    }

    if (defaultValue?.includes('#')) {
      return { label: defaultValue, value: defaultValue };
    }

    return options.find((option) => option.value === defaultValue) || options[0];
  }, [options, defaultValue]);

  const [customColor, setCustomColor] = useState<ColorOption[]>([]);
  const [selected, setSelected] = useState<ColorOption>(defaultSelected);

  const [color, setColor] = useState<string>('');

  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState({ top: 0, left: 0, width: 0 });
  const labelRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();

  const handleClickOutside = () => {
    if (color) {
      onPickerChange(color);
      onChangeHandler(color);
    }

    setIsOpened(false);
    setColor('');
  };

  const containerRef = useClickOutside(handleClickOutside);

  const toggleVisibility = useCallback((visible?: boolean) => {
    setIsOpened((previous) => {
      const isVisible = visible ?? !previous;

      if (isVisible && labelRef.current) {
        const boundingClientRect = labelRef.current.getBoundingClientRect();

        setDimensions({
          left: boundingClientRect.left,
          top: boundingClientRect.top + boundingClientRect.height + theme.Spacing.Size.XXSmall,
          width: boundingClientRect.width,
        });
      }

      return isVisible;
    });
  }, []);

  const onPickerChange = useCallback(
    (value: string) => {
      setColor(value);

      if (customColor.find((color) => color.value === value)) {
        return;
      }

      setCustomColor((prev) => {
        const updatedCustomColor = prev.concat({ label: value, value });

        return updatedCustomColor.slice(-limit);
      });
    },
    [setColor, customColor, setCustomColor, limit]
  );

  const onChangeHandler = useCallback(
    (selectedValue: string) => {
      toggleVisibility(false);

      if (selectedValue !== selected.value || selectedValue === 'transparent') {
        const selectedColor = (options.concat(customColor) as ColorOption[]).find(
          (option) => option.value === selectedValue
        );

        setSelected(
          selectedColor ?? {
            label: selectedValue,
            value: selectedValue,
          }
        );

        onChange?.(selectedValue);
      }
    },
    [toggleVisibility, selected.value, options, customColor, onChange]
  );

  const onHandleContainerScroll = () => {
    setIsOpened(false);
  };

  useScrollRefEventListener({ scrollRef, onHandleContainerScroll });

  if (variant === 'palette') {
    return (
      <StyledPaletteContainer>
        <DropdownNext
          displayMode="grid"
          gridColumns={6}
          state={{ open: isOpened }}
          options={options.map((option) => {
            return {
              type: 'custom',
              value: option.value,
              children: (
                <StyledPaletteDropdown
                  onClick={(event) => {
                    option.value === '' && event.stopPropagation();
                  }}
                >
                  <ColorBullet
                    color={option.value}
                    isPicker={option.label === 'picker'}
                    isSelected={option.value === selected.value}
                    onChange={(value) => {
                      option.label === 'picker' ? setColor(value) : onChangeHandler(option.value);
                    }}
                    onChangeComplete={onChangeHandler}
                    variant="rounded"
                    size="Large"
                    readOnly={option.label !== 'picker'}
                    placement="leftTop"
                    title={
                      option.label === 'picker'
                        ? customColorLabel
                        : option.value === 'transparent'
                        ? transparentLabel
                        : undefined
                    }
                  />
                </StyledPaletteDropdown>
              ),
            };
          })}
          trigger="click"
          onClick={() => setIsOpened(!isOpened)}
        >
          <StyledPaletteSelectorContainer onClick={() => setIsOpened(!isOpened)}>
            <ColorBullet
              color={selected.value || options[0].value}
              variant="rounded"
              size="Large"
              readOnly={true}
              isPicker={selected.value === 'custom'}
              placement="leftTop"
            />
          </StyledPaletteSelectorContainer>
        </DropdownNext>
      </StyledPaletteContainer>
    );
  }

  return (
    <StyledContainer ref={containerRef as RefObject<HTMLDivElement>}>
      <DropdownNext
        trigger="click"
        state={{ open: isOpened }}
        width="matchButton"
        widthVariant="fullWidth"
        options={[
          {
            value: 'color',
            type: 'custom',
            children: (
              <StyledDropdownOptionsContainer>
                {options.concat(customColor).map((option, index) => {
                  return (
                    <StyledDropdownOptionWrapper
                      key={`dropdown-option-${option.label}-${index}`}
                      onClick={(event) => {
                        option.value === '' && event.stopPropagation();
                      }}
                    >
                      <ColorBullet
                        color={option.value}
                        isPicker={option.label === 'picker'}
                        isSelected={option.value === selected.value}
                        onChange={(value) => {
                          option.label === 'picker'
                            ? setColor(value)
                            : onChangeHandler(option.value);
                        }}
                        readOnly={option.label !== 'picker'}
                        size="Large"
                        title={
                          option.label === 'picker'
                            ? customColorLabel
                            : option.value === 'transparent'
                            ? transparentLabel
                            : undefined
                        }
                        variant="rounded"
                        placement="topRight"
                      />
                    </StyledDropdownOptionWrapper>
                  );
                })}
              </StyledDropdownOptionsContainer>
            ),
          },
        ]}
      >
        <StyledLabelContainer onClick={() => toggleVisibility()} ref={labelRef} isOpened={isOpened}>
          <StyledColorIconPrefix>
            {!selected.value && placeholder ? (
              /**
               * This is a workaround to prevent the dropdown from opening when clicking on the color picker icon.
               * It allows the user to select a custom color without immediately opening the dropdown.
               */
              <div onClick={(event) => event.stopPropagation()}>
                <ColorBullet
                  color=""
                  isPicker
                  onChange={(value) => setColor(value)}
                  variant="rounded"
                  size="Large"
                  placement="leftTop"
                  title={placeholder}
                />
              </div>
            ) : selected.value === 'transparent' ? (
              <Icon name="library---20" color={theme.Color.Neutrals.Dark} size="XSmall" />
            ) : (
              <ColorBullet
                color={selected.value}
                variant="rounded"
                size="Large"
                readOnly={true}
                placement="topRight"
              />
            )}
          </StyledColorIconPrefix>

          <Text size={'Body'}>{!selected.value && placeholder ? placeholder : selected.label}</Text>

          <StyledIconWrapper>
            <Icon
              name="chevron---20"
              angle={isOpened ? 180 : 0}
              color={theme.Color.Neutrals.Dark}
            />
          </StyledIconWrapper>
        </StyledLabelContainer>
      </DropdownNext>
    </StyledContainer>
  );
}
